import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  WorkbenchPreferences,
  WorkspaceDeletionJobView,
  WorkspaceInvitationView,
  WorkspaceMemberView,
  WorkspaceView,
  WorkspaceRole,
  WorkspaceGrant,
  WorkspaceResourceScope,
} from 'shared';
import { DEFAULT_WORKSPACE_MEMBER_PREFERENCES, workspaceCan } from 'shared';
import { workspaceApi } from '@/api/workspace.api';
import { useAuthStore } from '@/stores/auth.store';
import type { ResourceChangedEvent } from '@/composables/useCollaborationSocket';
import { registerResourceStore } from '@/stores/realtimeDispatcher';

export const useWorkspaceStore = defineStore(
  'workspace',
  () => {
    const auth = useAuthStore();
    const entities = ref<Record<string, WorkspaceView>>({});
    const archivedEntities = ref<Record<string, WorkspaceView>>({});
    const membersByWorkspace = ref<Record<string, WorkspaceMemberView[]>>({});
    const rolesByWorkspace = ref<Record<string, WorkspaceRole[]>>({});
    const invitations = ref<WorkspaceInvitationView[]>([]);
    const deletionJobs = ref<Record<string, WorkspaceDeletionJobView>>({});
    const currentWorkspaceId = ref<string | null>(null);
    const workbenchSimpleMode = ref(false);
    const workbenchPreferenceDraft = ref<{
      workspaceId: string;
      values: Partial<WorkbenchPreferences>;
    } | null>(null);
    const initialized = ref(false);
    const opening = ref(false);
    const openError = ref('');
    let openVersion = 0;
    const current = computed(() =>
      currentWorkspaceId.value ? (entities.value[currentWorkspaceId.value] ?? null) : null,
    );
    function myMemberFor(workspaceId: string | null): WorkspaceMemberView | null {
      if (!workspaceId || !auth.user?.id) return null;
      return (
        membersByWorkspace.value[workspaceId]?.find(
          (member) => member.userId === auth.user?.id && member.status === 'active',
        ) ?? null
      );
    }
    const currentMember = computed(() => myMemberFor(currentWorkspaceId.value));
    function workbenchPreferencesFor(workspaceId: string | null): WorkbenchPreferences {
      return {
        ...DEFAULT_WORKSPACE_MEMBER_PREFERENCES,
        ...myMemberFor(workspaceId)?.preferences,
        ...(workbenchPreferenceDraft.value?.workspaceId === workspaceId
          ? workbenchPreferenceDraft.value.values
          : {}),
      };
    }
    function workbenchPreferencesDirtyFor(workspaceId: string | null): boolean {
      return workbenchPreferenceDraft.value?.workspaceId === workspaceId;
    }
    const workbenchPreferences = computed(() => workbenchPreferencesFor(currentWorkspaceId.value));
    const workbenchPreferencesDirty = computed(() =>
      workbenchPreferencesDirtyFor(currentWorkspaceId.value),
    );

    function previewWorkbenchPreferences(
      workspaceId: string,
      values: Partial<WorkbenchPreferences>,
    ): void {
      workbenchPreferenceDraft.value = {
        workspaceId,
        values: {
          ...(workbenchPreferenceDraft.value?.workspaceId === workspaceId
            ? workbenchPreferenceDraft.value.values
            : {}),
          ...values,
        },
      };
    }
    function clearWorkbenchPreview(workspaceId: string): void {
      if (workbenchPreferenceDraft.value?.workspaceId === workspaceId)
        workbenchPreferenceDraft.value = null;
    }

    function upsert(workspace: WorkspaceView): void {
      entities.value[workspace.id] = { ...entities.value[workspace.id], ...workspace };
    }
    function upsertArchived(workspace: WorkspaceView): void {
      archivedEntities.value[workspace.id] = workspace;
    }
    function can(grant: string, scope?: WorkspaceResourceScope): boolean {
      const currentId = currentWorkspaceId.value;
      if (!currentId) return false;
      const member = currentMember.value;
      return Boolean(member && workspaceCan(member, grant as WorkspaceGrant, scope));
    }
    async function refreshRoles(workspaceId: string): Promise<void> {
      rolesByWorkspace.value[workspaceId] = await workspaceApi.roles(workspaceId);
    }

    async function refreshAll(): Promise<void> {
      const workspaces = await workspaceApi.list('active');
      // 即使服务端被旧版本或代理错误地返回了混合数据，切换器也绝不能显示归档工作台。
      entities.value = Object.fromEntries(
        workspaces
          .filter((workspace) => workspace.status === 'active')
          .map((workspace) => [workspace.id, workspace]),
      );
      // 持久化的当前工作台可能已被删除或归档，不能继续把失效 ID 当作选中项。
      if (currentWorkspaceId.value && !entities.value[currentWorkspaceId.value])
        currentWorkspaceId.value = null;
      initialized.value = true;
    }

    async function refreshArchived(): Promise<void> {
      const workspaces = await workspaceApi.list('archived');
      // 管理页的归档区只接受已归档或正在删除的工作台，避免与可用区重复。
      archivedEntities.value = Object.fromEntries(
        workspaces
          .filter((workspace) => workspace.status === 'archived' || workspace.status === 'deleting')
          .map((workspace) => [workspace.id, workspace]),
      );
    }

    async function refreshInvitations(): Promise<void> {
      invitations.value = await workspaceApi.invitations();
    }

    async function open(workspaceId: string): Promise<void> {
      const version = ++openVersion;
      const userId = auth.user?.id;
      opening.value = true;
      openError.value = '';
      try {
        const [workspace, members] = await Promise.all([
          workspaceApi.get(workspaceId), workspaceApi.members(workspaceId),
        ]);
        if (version !== openVersion || auth.user?.id !== userId) return;
        upsert(workspace);
        membersByWorkspace.value[workspaceId] = members;
        currentWorkspaceId.value = workspaceId;
        void workspaceApi.activate(workspaceId).catch(() => undefined);
      } catch (cause) {
        if (version === openVersion) openError.value = (cause as Error).message || '打开工作台失败，请重试';
        throw cause;
      } finally {
        if (version === openVersion) opening.value = false;
      }
    }

    async function refreshMembers(workspaceId = currentWorkspaceId.value): Promise<void> {
      if (!workspaceId) return;
      membersByWorkspace.value[workspaceId] = await workspaceApi.members(workspaceId);
    }

    async function updateMyPreferences(
      workspaceId: string,
      input: Partial<WorkspaceMemberView['preferences']>,
    ): Promise<WorkspaceMemberView> {
      const userId = auth.user?.id;
      const updated = await workspaceApi.updateMyPreferences(workspaceId, input);
      if (auth.user?.id !== userId) return updated;
      const members = membersByWorkspace.value[workspaceId];
      const index = members?.findIndex((member) => member.id === updated.id) ?? -1;
      if (members && index >= 0)
        members[index] = {
          ...updated,
          preferences: {
            ...members[index].preferences,
            ...Object.fromEntries(
              (Object.keys(input) as Array<keyof WorkspaceMemberView['preferences']>)
                .filter((key) => input[key] !== undefined)
                .map((key) => [key, updated.preferences[key]]),
            ),
          },
        };
      return updated;
    }

    async function saveWorkbenchPreferences(workspaceId: string): Promise<void> {
      const draft = workbenchPreferenceDraft.value;
      if (draft?.workspaceId !== workspaceId) return;
      await updateMyPreferences(workspaceId, draft.values);
      if (workbenchPreferenceDraft.value === draft) clearWorkbenchPreview(workspaceId);
    }

    async function handleMemberRealtimeEvent(event: ResourceChangedEvent): Promise<void> {
      const currentUserId = auth.user?.id;
      const changedMember = event.payload.data as WorkspaceMemberView | undefined;
      const workspaceId = event.workspaceId ?? changedMember?.workspaceId;
      if (!workspaceId) return;
      if (changedMember && changedMember.userId === currentUserId && entities.value[workspaceId]) {
        entities.value[workspaceId] = {
          ...entities.value[workspaceId],
          memberRole: changedMember.role,
        };
      }
      if (!membersByWorkspace.value[workspaceId]) return;
      const members = membersByWorkspace.value[workspaceId];
      const index = members.findIndex((member) => member.id === event.payload.resourceId);
      if (event.payload.operation === 'deleted') {
        if (index >= 0) members.splice(index, 1);
        return;
      }
      if (event.payload.data) {
        const member = event.payload.data as unknown as WorkspaceMemberView;
        if (index >= 0) members[index] = member;
        else members.push(member);
        return;
      }
      await refreshMembers(workspaceId);
    }

    async function handleWorkspaceRealtimeEvent(event: ResourceChangedEvent): Promise<void> {
      const data = event.payload.data as WorkspaceView | undefined;
      const id = event.payload.resourceId;
      if (event.payload.operation === 'deleted') {
        delete entities.value[id];
        if (currentWorkspaceId.value === id) currentWorkspaceId.value = null;
        return;
      }
      if (!data?.id) return;
      if (data.status === 'active') {
        upsert(data);
        delete archivedEntities.value[data.id];
      } else {
        upsertArchived(data);
        delete entities.value[data.id];
        if (currentWorkspaceId.value === data.id) currentWorkspaceId.value = null;
      }
    }

    registerResourceStore('workspace-member', { handleRealtimeEvent: handleMemberRealtimeEvent });
    registerResourceStore('workspace-access', { async handleRealtimeEvent(event) {
      const workspaceId = event.workspaceId ?? event.payload.resourceId;
      if (!membersByWorkspace.value[workspaceId]) return;
      await Promise.all([refreshMembers(workspaceId), refreshRoles(workspaceId)]);
      window.dispatchEvent(new CustomEvent('workspace:permissions-changed', { detail: { workspaceId } }));
    } });
    registerResourceStore('workspace', { handleRealtimeEvent: handleWorkspaceRealtimeEvent });
    function close(): void {
      ++openVersion;
      opening.value = false;
      openError.value = '';
      currentWorkspaceId.value = null;
      workbenchPreferenceDraft.value = null;
    }
    return {
      entities,
      archivedEntities,
      membersByWorkspace,
      rolesByWorkspace,
      refreshRoles,
      invitations,
      deletionJobs,
      currentWorkspaceId,
      workbenchSimpleMode,
      workbenchPreferences,
      workbenchPreferencesDirty,
      myMemberFor,
      workbenchPreferencesFor,
      workbenchPreferencesDirtyFor,
      previewWorkbenchPreferences,
      clearWorkbenchPreview,
      saveWorkbenchPreferences,
      current,
      currentMember,
      initialized,
      opening,
      openError,
      upsert,
      upsertArchived,
      can,
      refreshAll,
      refreshArchived,
      refreshInvitations,
      refreshMembers,
      updateMyPreferences,
      open,
      close,
    };
  },
  { persist: { pick: ['currentWorkspaceId'] } },
);
